/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { CoreNetworking } from '@airasia-rn/ui';
import { StationsSearchResponse } from '../components/OriginDestinationSelector/types';
import { QueryType } from '../components/OriginDestinationSelector/types';
import { StationDataObject } from '../types/FlightSearch.types';
import EnvironmentURLs, { parseEnv } from '../util/EnvironmentURLs';

declare const __DEV__: boolean;

export type Environment = 'stg' | 'prd';

/**
 * Travel Search Service (stations) API raw response types.
 * Ref: go-travel-search/docs/openapi.yaml -> ApiSearchResponse / StationBase
 */
interface StationBase {
  cityCode: string;
  cityName: string;
  stationCode: string;
  stationName: string;
  countryCode: string;
  countryName: string;
  stationType: string;
  timeZone?: string;
  aaFlight?: boolean;
  dest?: string;
  provider?: string;
  id?: string;
  iconName?: string;
  airportName?: string;
  alternativeName?: string;
  pinYin?: string;
  tag?: string;
  weightage?: number;
  latitude?: number;
  longitude?: number;
  isCity?: boolean;
  // Nested airports under a city result - the live API returns these even
  // though they aren't in the openapi.yaml StationBase schema.
  stations?: StationBase[];
}

interface ApiSearchResponse {
  data?: {
    stations?: StationBase[];
  };
  errors?: { code?: string; message?: string; field?: string }[];
}

const mapStationToStationDataObject = (
  station: StationBase,
): StationDataObject => ({
  StationType: station.stationType,
  CityName: station.cityName,
  CountryName: station.countryName,
  StationCode: station.stationCode,
  CityCode: station.cityCode,
  CountryCode: station.countryCode,
  StationName: station.stationName,
  TimeZone: station.timeZone,
  Provider: station.provider,
  iconName: station.iconName,
  Dest: station.dest,
  AAFlight: station.aaFlight,
  AirportName: station.airportName,
  AlternativeName: station.alternativeName,
  PinYin: station.pinYin,
  Tag: station.tag,
  Weightage: station.weightage,
  Lat: station.latitude,
  Long: station.longitude,
  IsCity: station.isCity ?? station.stationType === 'C',
  Stations: station.stations?.map(mapStationToStationDataObject),
});

export class StationSearchApiService {
  constructor(private environment: Environment) {}
  private getStationStationUrl(): string {
    return EnvironmentURLs.STATION_SEARCH[parseEnv(this.environment)];
  }
  async fetchStationSearch(
    queryParams: QueryType,
    signal?: AbortSignal,
  ): Promise<StationsSearchResponse> {
    const url = this.getStationStationUrl();

    const stationSearchQueryParams = new URLSearchParams(
      Object.entries(queryParams).reduce(
        (acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
    ).toString();

    try {
      const response = await CoreNetworking.get(
        `${url}?${stationSearchQueryParams}`,
        { signal },
      );

      const data = response?.data as ApiSearchResponse;
      return (data?.data?.stations ?? []).map(mapStationToStationDataObject);
    } catch (err) {
      if (__DEV__) {
        console.log('❌ [Station Search API] Fetch ERROR:', err);
      }
      throw err;
    }
  }
}
